<script>
export default {
  name: 'WhatsAppCampaignEditPage',
};
</script>

<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const campaign = ref(null);
const isFetching = ref(false);

const handleClose = () => {
  const campaignCategory =
    route.query.category ||
    campaign.value?.whatsapp_template_category ||
    'utility';
  router.push({
    name: 'campaigns_whatsapp_category_index',
    params: { campaignCategory },
    query: { inbox_id: route.params.inboxId },
  });
};

const fetchCampaign = async () => {
  isFetching.value = true;
  try {
    const response = await CampaignsAPI.show(route.params.campaignId);
    campaign.value = response.data;
  } catch {
    useAlert(
      t('CAMPAIGN.WHATSAPP.EDIT.API.ERROR_MESSAGE', 'Unable to load campaign.')
    );
    handleClose();
  } finally {
    isFetching.value = false;
  }
};

const updateCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/update', {
      id: campaign.value.id,
      ...campaignDetails,
    });
    useAlert(
      t('CAMPAIGN.WHATSAPP.EDIT.API.SUCCESS_MESSAGE', 'Campaign saved.')
    );
    handleClose();
  } catch {
    useAlert(
      t('CAMPAIGN.WHATSAPP.EDIT.API.SAVE_ERROR', 'Unable to save campaign.')
    );
  }
};

onMounted(fetchCampaign);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-4 bg-white dark:bg-n-solid-2 border-b border-n-weak dark:border-n-strong shrink-0 sm:px-6"
    >
      <div class="w-full max-w-7xl mx-auto">
        <div
          class="flex flex-col justify-center w-full gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-3 min-w-0">
            <Button
              icon="i-lucide-arrow-left"
              variant="ghost"
              color="slate"
              size="sm"
              class="flex-shrink-0"
              @click="handleClose"
            />
            <span class="block text-heading-1 text-n-slate-12 truncate">
              {{ t('CAMPAIGN.WHATSAPP.EDIT.TITLE', 'Edit WhatsApp campaign') }}
            </span>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-4 overflow-y-auto py-6 sm:px-6">
      <div class="w-full max-w-7xl mx-auto">
        <div v-if="isFetching" class="flex items-center justify-center py-12">
          <Spinner />
        </div>
        <WhatsAppCampaignForm
          v-else-if="campaign"
          :campaign="campaign"
          @submit="updateCampaign"
          @cancel="handleClose"
        />
      </div>
    </main>
  </section>
</template>
